"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Capability = {
  number: string;
  title: string;
  description: string;
  image: string;
};
const capabilities: Capability[] = [{
  number: "01",
  title: "Infrastructure Project Management",
  description: "InfraPM supports infrastructure projects by organizing scopes, schedules, partners, documentation, and decision points. We help clients keep complex work moving by translating technical, funding, and administrative requirements into practical next steps.\n\nOur role is not to replace engineers, contractors, or public staff. Our role is to strengthen coordination, reduce confusion, and help project teams stay aligned from planning through implementation.",
  image: "https://infrapm.com/wp-content/uploads/2026/07/West-Texas-Pipe-CGPT.png"
}, {
  number: "02",
  title: "Grant Administration & Compliance",
  description: "InfraPM helps public agencies and utilities manage post-award grant requirements, including reporting, reimbursement support, documentation, coordination, and closeout preparation. We create organized compliance systems so funding requirements are understood, tracked, and addressed before they become problems.\n\nOur support is built for small and mid-sized public entities that need reliable grant administration capacity without adding unnecessary complexity.",
  image: "https://infrapm.com/wp-content/uploads/2026/07/West-Texas-Environmental-CGPT.png"
}, {
  number: "03",
  title: "Public Funding",
  description: "InfraPM helps utilities and local governments identify, pursue, and manage public funding for critical infrastructure projects. We translate grant requirements, agency expectations, and project needs into practical applications, compliance documents, and delivery strategies that help communities move from funding opportunity to implementation.\n\nThis includes funding research, application strategy, narrative development, attachment coordination, board-action support, and submission-ready materials.",
  image: "https://infrapm.com/wp-content/uploads/2026/07/public-funding.jpg"
}, {
  number: "04",
  title: "Stakeholder Coordination",
  description: "InfraPM helps align local governments, utilities, engineers, funding agencies, and regional partners, around clear project goals, roles, and next steps. We support practical coordination that keeps projects moving, reduces confusion, and turns technical, funding, and policy requirements into shared action.\n\nThis service is especially useful when a project depends on multiple agencies, funding sources, approvals, or public-facing decisions.",
  image: "https://infrapm.com/wp-content/uploads/2026/07/Public-Meeting-CGPT.png"
}, {
  number: "05",
  title: "Procurement & Capture Strategy",
  description: "InfraPM supports private-sector infrastructure teams pursuing public-sector work by tracking realistic opportunities, clarifying local context, and preparing stronger capture strategies. We help engineers, contractors, consultants, and partners understand where they fit, what matters to the buyer, and how to position before the formal deadline.\n\nOur capture support focuses on practical intelligence, pursuit discipline, local-government context, and proposal readiness.\n",
  image: "https://infrapm.com/wp-content/uploads/2026/07/Grants-Capture-CGPT.png"
}, {
  number: "06",
  title: "Regional Intelligence",
  description: "InfraPM tracks civic, funding, policy, procurement, and capital planning signals that matter to both public agencies and private-sector infrastructure teams. This intelligence helps local governments plan ahead while helping engineers, contractors, and partners identify realistic opportunities, understand local context, and prepare stronger capture strategies.\n\nWe focus on useful signals, not noise: funding windows, board actions, procurement movement, legislative changes, infrastructure priorities, and regional project activity.",
  image: "https://infrapm.com/wp-content/uploads/2026/07/20260320_212826-scaled.jpg"
}];
function CapabilityRow({
  capability,
  index
}: {
  capability: Capability;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const delay = index * 120;
  const isEven = index % 2 === 0;
  return <div data-index={index} ref={ref} style={{
    transitionDelay: isVisible ? `${delay}ms` : "0ms"
  }} className="grid grid-cols-1 gap-8 transition-all duration-[480ms] ease-out items-center opacity-100 translate-y-0 ![padding:0px_0px_64px_0px] md:grid-cols-12 md:gap-16">
      <div className={`md:col-span-6 ${isEven ? "md:order-2" : "md:order-1"}`}>
        <h3 className="font-default font-medium mt-4" style={{
        fontSize: "clamp(1.25rem, 1.2vw + 0.75rem, 1.625rem)",
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
        color: "#18334e"
      }}>
          {capability.title}
        </h3>

        <p className="font-default text-foreground/80 mt-4 max-w-md whitespace-pre-line" style={{
        fontSize: "1rem",
        lineHeight: 1.6
      }}>
          {capability.description}
        </p>
      </div>

      <div className={`md:col-span-6 flex items-center justify-center ${isEven ? "md:order-1" : "md:order-2"}`}>
        <div className="group w-full aspect-[4/3] overflow-hidden">
          <img src={capability.image} data-wvc-srcset={`${capability.image}-wvc-srcset`} data-wvc-sizes={`${capability.image}-wvc-sizes`} alt={capability.title} loading="lazy" className="w-full h-full object-cover grayscale transition-all duration-500 ease-out group-hover:grayscale-0 group-hover:scale-105" />
        </div>
      </div>
    </div>;
}
export default function Infrastructure() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="15125" id="infrastructure-project-management" className="bg-background py-16 md:py-44 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 w-full max-w-full">
          <div ref={headingRef} className={`w-full max-w-full md:col-span-8 transition-all duration-[480ms] ease-out ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <h1 className="font-default w-full max-w-full md:max-w-[900px] break-words" style={{
            fontSize: "clamp(2.75rem, 10vw, 5.5rem)",
            fontWeight: 500,
            letterSpacing: "-0.025em",
            lineHeight: 0.98,
            color: "#18334e"
          }}>
              Turning infrastructure needs into actionable projects.
            </h1>

            <p className="mt-8 w-full max-w-full md:max-w-2xl text-base md:text-xl text-foreground/70 leading-relaxed">
              InfraPM helps public agencies, utilities, private firms, and
              international companies move infrastructure opportunities from
              early concept to funded, organized, and actionable work. We
              provide the strategy, intelligence, documentation, and coordination
              needed to navigate public funding, procurement, compliance, and
              project delivery with greater clarity.
            </p>
          </div>
        </div>

        <div className="mt-16 md:mt-24">
          {capabilities.map((capability, i) => <div key={capability.number} data-index={i}>
              <CapabilityRow capability={capability} index={i} />
              {i < capabilities.length - 1 && <div className="h-px w-full bg-border" />}
            </div>)}
        </div>
      </div>
    </section>;
}